'use client';

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Condition } from '@skin-seoul/utils';
import { FibosearchCombobox } from '@/components/fibosearch/FibosearchCombobox/FibosearchCombobox';
import { CollectionGroup } from '@/components/fibosearch/FibosearchCombobox/CollectionGroup';
import { CollectionList } from '@/components/fibosearch/FibosearchCombobox/CollectionList';
import { useFiboService } from '@skin-seoul/service';
import { debounce } from 'es-toolkit';
import { useSearchParams } from 'next/navigation';
import { Flex, IconButton, Spinner } from '@skin-seoul/design-system';
import LucideIcon from '@/components/ui/LucideIcon';
import SearchHeaderInput from '@/containers/search/components/SearchHeaderInput';
import { useSafeRouter } from '@/hooks/useSafeRouter';
import { env } from '@/utils/env';
import { createFiboCollection } from '../../components/fibosearch/FibosearchCombobox/utils';
type SearchContentProps = {
  onClose: () => void;
};
const SearchContent = ({
  onClose
}: SearchContentProps) => {
  const searchParams = useSearchParams();
  const searchValue = searchParams.get('s') ?? '';
  const [inputValue, setInputValue] = useState(searchValue);
  const [debouncedValue, setDebouncedValue] = useState(searchValue);
  const inputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    setTimeout(() => {
      inputRef.current?.focus();
    }, 0);
  }, []);
  const {
    useFiboSearch
  } = useFiboService();
  const {
    data: fiboSearchData,
    isLoading
  } = useFiboSearch({
    s: debouncedValue
  });
  const suggestions = useMemo(() => fiboSearchData?.suggestions ?? [], [fiboSearchData]);
  const {
    collection,
    groupedCollection,
    isGroupedCollection,
    convertedCollectionComboboxItems
  } = useMemo(() => createFiboCollection(suggestions), [suggestions]);
  const router = useSafeRouter();
  const handleNavigate = useCallback((url: string) => {
    router.push(url);
  }, [router]);
  const handleSearch = () => {
    handleNavigate(`${env('NEXT_PUBLIC_ENV_FRONTEND_ORIGIN')}/search/?s=${encodeURIComponent(inputValue)}`);
    onClose();
  };
  useEffect(() => {
    const handleDebounceValue = debounce((value: string) => {
      setDebouncedValue(value);
    }, 200);
    handleDebounceValue(inputValue);
    return () => {
      handleDebounceValue.cancel();
    };
  }, [inputValue]);
  return <FibosearchCombobox.Root collection={collection} disableLayer inputBehavior="autohighlight" loopFocus autoFocus immediate closeOnSelect={false} open placeholder="Find your products" selectionBehavior="preserve" positioning={{
    offset: {
      mainAxis: 1,
      crossAxis: 0
    }
  }} data-sentry-element="FibosearchCombobox.Root" data-sentry-component="SearchContent" data-sentry-source-file="SearchContent.tsx">
      <FibosearchCombobox.Control display="flex" width="100%" data-sentry-element="FibosearchCombobox.Control" data-sentry-source-file="SearchContent.tsx">
        <Flex height="45px" width="100%" alignItems="center" data-sentry-element="Flex" data-sentry-source-file="SearchContent.tsx">
          <IconButton width="45px" height="45px" variant="ghost" aria-label="Back" onClick={onClose} data-sentry-element="IconButton" data-sentry-source-file="SearchContent.tsx">
            <LucideIcon name="ArrowLeftIcon" width="24px" height="24px" color="primary.600" data-sentry-element="LucideIcon" data-sentry-source-file="SearchContent.tsx" />
          </IconButton>
          <Flex flex="1" paddingRight="10px" data-sentry-element="Flex" data-sentry-source-file="SearchContent.tsx">
            <SearchHeaderInput ref={inputRef} autoFocus value={inputValue} onKeyDown={e => {
            if (e.key === 'Enter') {
              handleSearch();
            }
          }} onChange={e => setInputValue(e.target.value)} inputGroupProps={{
            endElement: <>
                    <Condition expression={isLoading} then={<Spinner color="primary.600" animationDuration="0.8s" />} />
                    <Condition expression={!isLoading && inputValue.length > 0} then={<LucideIcon name="X" color="neutral.600" onClick={() => setInputValue('')} />} />
                  </>
          }} data-sentry-element="SearchHeaderInput" data-sentry-source-file="SearchContent.tsx" />
          </Flex>
        </Flex>
      </FibosearchCombobox.Control>
      <Condition expression={inputValue.length > 0} then={<Condition expression={isGroupedCollection} then={<CollectionGroup groupedCollection={groupedCollection} inputValue={inputValue} onClose={onClose} />} else={<CollectionList collections={convertedCollectionComboboxItems} inputValue={inputValue} onClose={onClose} />} />} data-sentry-element="Condition" data-sentry-source-file="SearchContent.tsx" />
    </FibosearchCombobox.Root>;
};
export default SearchContent;